/**
 * usePrimaryLocale - Database-driven Primary Locale Resolution
 * 
 * Uses system_locale_configs as the single source of truth for:
 * - Determining if a locale is primary (e.g., zh-TW is primary, zh-HK is regional)
 * - Resolving regional locales to their primary locale (e.g., zh-HK → zh-TW)
 * 
 * NEVER use locale.split('-')[0] for content localization!
 * That naive approach incorrectly handles:
 * - zh-TW (Traditional Chinese) - IS a primary locale, shouldn't become 'zh'
 * - zh-HK (Hong Kong) - should map to zh-TW, not 'zh'
 */

import { useMemo, useCallback } from 'react';
import { useLocaleConfigs, LocaleConfig } from './useLocaleConfigs';

/**
 * Hook that provides database-driven primary locale resolution
 * 
 * @returns Object with:
 * - resolveToPrimary(locale): Resolves any locale to its primary locale
 * - isPrimary(locale): Checks if a locale is primary
 * - isLoading: Whether locale configs are still loading
 * - localeConfigs: Raw locale configs for advanced use cases
 */
export function usePrimaryLocale() {
  const { data: localeConfigs, isLoading } = useLocaleConfigs();

  /**
   * Resolve a locale to its primary locale using system_locale_configs
   * 
   * Examples:
   * - 'en-US' → 'en' (English US regional → English primary)
   * - 'zh-TW' → 'zh-TW' (Traditional Chinese IS primary, stays as-is)
   * - 'zh-HK' → 'zh-TW' (Hong Kong Chinese → Traditional Chinese)
   * - 'de-DE' → 'de' (German Germany → German primary)
   */
  const resolveToPrimary = useCallback((locale: string): string => {
    if (!locale) return 'en';
    
    // locale-logic: Bootstrap fallback for loading state - hardcoded zh-TW/zh-HK handling
    // These are the known exceptions where split('-')[0] is WRONG
    if (!localeConfigs?.length) {
      // Special cases: zh-TW and zh-HK should not be split
      if (locale === 'zh-TW') return 'zh-TW';
      if (locale === 'zh-HK') return 'zh-TW';
      // Default fallback during loading - use base language
      return locale.split('-')[0];
    }
    
    const config = localeConfigs.find((c: LocaleConfig) => c.locale === locale);
    
    if (!config) {
      // Unknown locale - try base language
      // locale-logic: Special handling for zh- variants (Traditional Chinese exceptions)
      if (locale === 'zh-TW' || locale === 'zh-HK' || locale === 'zh-Hant') {
        // Check if there's a zh-TW primary in configs
        const zhPrimary = localeConfigs.find((c: LocaleConfig) => 
          (c.locale === 'zh-TW' || c.locale === 'zh') && c.isPrimary
        );
        return zhPrimary?.locale || 'zh-TW';
      }
      return locale.split('-')[0];
    }
    
    if (config.isPrimary) {
      return locale; // zh-TW stays zh-TW
    }
    
    // Return the primary locale for this regional variant
    return config.primaryLocale || locale.split('-')[0];
  }, [localeConfigs]);

  /**
   * Check if a locale is a primary locale (not a regional variant)
   */
  const isPrimary = useCallback((locale: string): boolean => {
    if (!locale) return false;
    
    // Fallback for loading state
    if (!localeConfigs?.length) {
      // zh-TW is known to be primary
      if (locale === 'zh-TW') return true;
      // Simple locales without region are typically primary
      return !locale.includes('-');
    }
    
    const config = localeConfigs.find((c: LocaleConfig) => c.locale === locale);
    return config?.isPrimary ?? !locale.includes('-');
  }, [localeConfigs]);

  /**
   * Get the primary locale config for a given locale
   * Returns the config of the resolved primary locale
   */
  const getPrimaryConfig = useCallback((locale: string): LocaleConfig | null => {
    if (!localeConfigs?.length) return null;
    
    const primaryLocale = resolveToPrimary(locale);
    return localeConfigs.find((c: LocaleConfig) => c.locale === primaryLocale) || null;
  }, [localeConfigs, resolveToPrimary]);

  return { 
    resolveToPrimary, 
    isPrimary, 
    getPrimaryConfig,
    isLoading,
    localeConfigs,
  };
}

/**
 * Synchronous helper for cases where locale configs are already loaded
 * Used in edge functions after fetching configs
 * 
 * @param localeConfigs - Array of locale configs from system_locale_configs
 * @param locale - The locale to resolve
 * @returns The primary locale
 */
export function resolveToPrimarySync(
  localeConfigs: Array<{ locale: string; is_primary?: boolean; isPrimary?: boolean; primary_locale?: string | null; primaryLocale?: string | null }>,
  locale: string
): string {
  if (!locale) return 'en';
  
  // Handle known exceptions if configs not available
  // locale-logic: Bootstrap fallback for known Chinese primaries
  if (!localeConfigs?.length) {
    // Known Chinese primary locales
    if (locale === 'zh-TW') return 'zh-TW';
    if (locale === 'zh-HK') return 'zh-TW'; // HK maps to TW (Traditional)
    if (locale === 'zh-CN') return 'zh-CN';
    // locale-logic: Bootstrap fallback - base language when configs unavailable
    return locale.split('-')[0];
  }
  
  const config = localeConfigs.find(c => c.locale === locale);
  
  if (!config) {
    // locale-logic: chinese-fallback - Unknown Chinese variants map to zh-TW (Traditional)
    // Check for known Chinese locale codes explicitly
    const CHINESE_CODES = ['zh-TW', 'zh-CN', 'zh-HK', 'zh-SG', 'zh'];
    const baseLang = locale.split('-')[0];
    if (baseLang === 'zh' || CHINESE_CODES.includes(locale)) {
      const zhPrimary = localeConfigs.find(c => 
        (c.locale === 'zh-TW' || c.locale === 'zh-CN' || c.locale === 'zh') && 
        (c.is_primary || c.isPrimary)
      );
      return zhPrimary?.locale || 'zh-TW';
    }
    // locale-logic: Bootstrap fallback - base language for unknown locales
    return baseLang;
  }
  
  // Check isPrimary (camelCase from frontend) or is_primary (snake_case from DB)
  const isPrimary = config.isPrimary ?? config.is_primary;
  if (isPrimary) {
    return locale;
  }
  
  // Return the primary locale for this regional variant
  const primaryLocale = config.primaryLocale ?? config.primary_locale;
  // locale-logic: Bootstrap fallback - use base language if no primary_locale set
  return primaryLocale || locale.split('-')[0];
}
